/** @jsxImportSource @emotion/react */
import type { ApiScanVideoSubmission } from '@kaiizen/kai-pro-api';
import { getScanGroup } from '@kaiizen/kai-scan-template-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useKaiProApiClient, useScanEditingContextSlice } from '@/Client/State';
import { FlyOut } from '@/Client/Widgets';

import { VideoPlaybackPanel } from './playback/VideoPlaybackPanel';
import type { ScanAudioAttachScope } from './scanAudioScopeLib';
import { formatVideoFileSize, formatVideoTimestamp, getSubmissionSubtitle } from './scanVideoFormatLib';
import { useVideoToolboxActivityLog } from './useVideoToolboxActivityLog';
import { useVideoToolboxFrameCapture } from './useVideoToolboxFrameCapture';
import { useVideoToolboxSpeechListening } from './useVideoToolboxSpeechListening';
import { useVideoToolboxVoiceScope } from './useVideoToolboxVoiceScope';
import type { VideoToolboxScopeDisplayNames } from './videoToolboxActivityLogLib';
import { VideoToolboxChat } from './VideoToolboxChat';
import { VideoToolboxClipbar } from './VideoToolboxClipbar';
import { resolveVideoToolboxClipReadStatus } from './videoToolboxClipReadStatusLib';
import { countScanVideoClipStills } from './videoToolboxClipStillsLib';
import { VideoToolboxComposer } from './VideoToolboxComposer';
import {
  DEFAULT_VIDEO_TOOLBOX_PANEL_STYLES,
  VIDEO_TOOLBOX_DRAWER_STYLE,
  type VideoToolboxPanelStyles,
} from './videoToolboxPanelStyles';
import { getRecentGroupPhotosForProposal } from './videoToolboxProposalPhotoLib';
import {
  collectVideoToolboxThreadHistory,
  isPersistedVideoToolboxActivityEntry,
} from './videoToolboxThreadHistoryLib';

export type VideoToolboxPanelProps = {
  open: boolean;
  submission?: ApiScanVideoSubmission;
  /** Scope-aware seek target from scan `videoOffset.startSeconds`. */
  initialPlaybackStartSeconds?: number;
  /** Group/task the editor is on: gates scope tools and is where captures and voice edits land. */
  attachScope?: ScanAudioAttachScope;
  /** Display-only scope names for capture chat rows. */
  scopeDisplayNames?: VideoToolboxScopeDisplayNames;
  styles?: VideoToolboxPanelStyles;
  onClose: () => void;
};

/** Right-side flyout for walkthrough playback (scope editor). */
export const VideoToolboxPanel = ({
  open,
  submission,
  initialPlaybackStartSeconds,
  attachScope = {},
  scopeDisplayNames,
  styles = DEFAULT_VIDEO_TOOLBOX_PANEL_STYLES,
  onClose,
}: VideoToolboxPanelProps) => {
  const kaiProApiClient = useKaiProApiClient();
  const { scanIsEditable, scanRoot } = useScanEditingContextSlice(['scanIsEditable', 'scanRoot']);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pausedForSpeechRef = useRef(false);

  const [playbackSubmission, setPlaybackSubmission] = useState<ApiScanVideoSubmission | undefined>();
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | undefined>();
  const [composerDraft, setComposerDraft] = useState('');
  const [listeningActive, setListeningActive] = useState(false);
  const [selectedProposalPhotoId, setSelectedProposalPhotoId] = useState<string | undefined>();
  const scanVideoId = playbackSubmission?.id ?? submission?.id;

  const canUseScopeTools = Boolean(scanIsEditable && attachScope.groupId);

  const {
    entries: activityLogEntries,
    recordPhotoCapture,
    recordUserMessage,
    recordSpeechUnsupported,
    recordVoiceApplied,
    recordVoiceProposed,
    recordVoiceRejected,
  } = useVideoToolboxActivityLog(open);

  const {
    enqueueUtterance,
    isThinking,
    micPaused,
    pendingProposal,
    confirmProposal,
    dismissProposal,
  } = useVideoToolboxVoiceScope({
    open,
    enabled: canUseScopeTools,
    attachScope,
    onVoiceApplied: recordVoiceApplied,
    onVoiceProposed: proposal =>
      recordVoiceProposed(
        proposal.proposalId,
        proposal.summary,
        proposal.utterance,
        proposal.confidence,
        proposal.jevDetail,
      ),
    onVoiceRejected: recordVoiceRejected,
  });

  const handleDictationActivity = useCallback((update: { interim?: string; utterance?: string }) => {
    const hasSpeech = Boolean(update.interim?.trim() || update.utterance?.trim());
    if (!hasSpeech) return;

    const video = videoRef.current;
    if (video && !pausedForSpeechRef.current) {
      pausedForSpeechRef.current = true;
      if (!video.paused) video.pause();
    }
  }, []);

  const {
    isListening,
    isSupported: isSpeechSupported,
    speechText,
  } = useVideoToolboxSpeechListening({
    open,
    enabled: canUseScopeTools,
    listeningActive,
    paused: micPaused,
    onSpeechActivityChange: handleDictationActivity,
    onSpeechUnsupported: recordSpeechUnsupported,
  });

  useEffect(() => {
    if (!open) {
      setComposerDraft('');
      setListeningActive(false);
      setSelectedProposalPhotoId(undefined);
      pausedForSpeechRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    setSelectedProposalPhotoId(undefined);
  }, [pendingProposal?.proposalId]);

  useEffect(() => {
    if (isListening && speechText) {
      setComposerDraft(speechText);
    }
  }, [isListening, speechText]);

  const handleToggleListening = useCallback(() => {
    if (listeningActive) {
      setListeningActive(false);
      return;
    }
    if (!isSpeechSupported) {
      recordSpeechUnsupported();
      return;
    }
    setListeningActive(true);
  }, [isSpeechSupported, listeningActive, recordSpeechUnsupported]);

  const handleSendMessage = useCallback(() => {
    const text = composerDraft.trim();
    if (!text || !canUseScopeTools || micPaused) return;

    setListeningActive(false);
    pausedForSpeechRef.current = false;
    recordUserMessage(text);
    setComposerDraft('');
    enqueueUtterance(text);
  }, [canUseScopeTools, composerDraft, enqueueUtterance, micPaused, recordUserMessage]);

  const { canCapture, captureAndUpload, isCapturing } = useVideoToolboxFrameCapture(
    attachScope,
    scanVideoId,
    scopeDisplayNames,
    event => recordPhotoCapture(event.photoId, event.destinationDisplayName),
  );
  const clipStillsCount = useMemo(
    () => countScanVideoClipStills(scanRoot, attachScope.groupId, scanVideoId),
    [attachScope.groupId, scanRoot, scanVideoId],
  );
  const threadHistoryEntries = useMemo(
    () => collectVideoToolboxThreadHistory(scanRoot, attachScope.groupId, scanVideoId),
    [attachScope.groupId, scanRoot, scanVideoId],
  );
  const proposalPhotoCandidates = useMemo(() => {
    if (!scanRoot || !attachScope.groupId || !pendingProposal) return [];
    const group = getScanGroup(scanRoot, attachScope.groupId);
    return getRecentGroupPhotosForProposal(group).map(photo => ({ photoId: photo.photoId }));
  }, [attachScope.groupId, pendingProposal, scanRoot]);
  const clipReadStatus = useMemo(
    () => resolveVideoToolboxClipReadStatus(scanRoot, attachScope.groupId),
    [attachScope.groupId, scanRoot],
  );
  const sessionChatEntries = useMemo(
    () => activityLogEntries.filter(entry => !isPersistedVideoToolboxActivityEntry(entry)),
    [activityLogEntries],
  );

  useEffect(() => {
    if (!open || !submission) {
      setPlaybackSubmission(undefined);
      setLoadErrorMessage(undefined);
      return;
    }

    setPlaybackSubmission(submission);
    setLoadErrorMessage(undefined);

    void kaiProApiClient.getScanVideo(submission.id)
      .then(freshSubmission => {
        setPlaybackSubmission(freshSubmission);
      })
      .catch(error => {
        setLoadErrorMessage(error instanceof Error ? error.message : 'Failed to load video');
      });
  }, [kaiProApiClient, open, submission]);

  const title = playbackSubmission?.originalFileName ?? submission?.originalFileName ?? 'Walkthrough video';
  const subtitle = playbackSubmission
    ? getSubmissionSubtitle(playbackSubmission)
    : submission
    ? `${formatVideoTimestamp(submission.createdAt)} · ${formatVideoFileSize(submission.sizeBytes)}`
    : '';
  const playbackUrl = playbackSubmission?.playbackUrl;

  const {
    panelBody,
    stickyVideo,
    playbackPanel,
    footHint,
    footHintError,
    panelActionFooter,
  } = styles;

  const composerFooter = canUseScopeTools && playbackUrl && !loadErrorMessage
    ? (
      <VideoToolboxComposer
        draft={composerDraft}
        onDraftChange={setComposerDraft}
        isListening={isListening}
        isSpeechSupported={isSpeechSupported}
        disabled={micPaused}
        onToggleListening={handleToggleListening}
        onSend={handleSendMessage}
      />
    )
    : loadErrorMessage
    ? (
      <div css={panelActionFooter}>
        <span css={[footHint, footHintError]}>{loadErrorMessage}</span>
      </div>
    )
    : undefined;

  return (
    <FlyOut
      open={open}
      flyOutClosed={onClose}
      drawerCss={VIDEO_TOOLBOX_DRAWER_STYLE}
      footer={composerFooter}
      footerBorder={false}
      title={title}
      subheader={subtitle}
    >
      <div css={panelBody} data-testid={'video-toolbox-panel'}>
        {loadErrorMessage && !playbackUrl
          ? <div css={footHint}>{loadErrorMessage}</div>
          : playbackUrl && scanVideoId
          ? (
            <>
              <div css={stickyVideo}>
                <VideoPlaybackPanel
                  key={`${scanVideoId}-${initialPlaybackStartSeconds ?? 'start'}`}
                  playbackUrl={playbackUrl}
                  density={'segment'}
                  scanRoot={scanRoot}
                  scanVideoId={scanVideoId}
                  highlightGroupId={attachScope.groupId}
                  videoRef={videoRef}
                  initialSeekStartSeconds={initialPlaybackStartSeconds}
                  videoTestId={'video-toolbox-player'}
                  styles={playbackPanel}
                  showCaptureButton={canCapture}
                  captureDisabled={isCapturing}
                  onCaptureFrame={() => {
                    const video = videoRef.current;
                    if (video) void captureAndUpload(video);
                  }}
                />
              </div>
              {canUseScopeTools && (
                <>
                  <VideoToolboxClipbar readStatus={clipReadStatus} />
                  <VideoToolboxChat
                    entries={sessionChatEntries}
                    historyEntries={threadHistoryEntries}
                    clipStillsCount={clipStillsCount}
                    isThinking={isThinking}
                    pendingProposalId={pendingProposal?.proposalId}
                    proposalPhotoCandidates={proposalPhotoCandidates}
                    selectedProposalPhotoId={selectedProposalPhotoId}
                    onSelectProposalPhotoId={setSelectedProposalPhotoId}
                    onConfirmProposal={() => void confirmProposal(selectedProposalPhotoId)}
                    onDismissProposal={dismissProposal}
                  />
                </>
              )}
            </>
          )
          : <div css={footHint}>Playback is unavailable for this video.</div>}
      </div>
    </FlyOut>
  );
};

export type { VideoToolboxPanelStyles } from './videoToolboxPanelStyles';
export { DEFAULT_VIDEO_TOOLBOX_PANEL_STYLES, VIDEO_TOOLBOX_DRAWER_STYLE } from './videoToolboxPanelStyles';
